import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { LessonContext } from '../services/backendService/types';
import { generateUniqueId } from '../utils/general';

export type MaterialAttachment = {
  passageId?: string;
  passageTitle?: string;
  classification?: 'embed' | 'inform';
  fileUploadId: string;
  fileName: string;
  mimetype: string;
  fileId: string;
  unprocessedAttachment?: Omit<MaterialAttachment, 'unprocessedAttachment'>;
  attachmentMethod: 'paste' | 'upload';
};

export const useUploadMaterialAttachment = (tenantId: string) => {
  const client = useQueryClient();
  const { mutate: upload, error, isLoading, isSuccess, data, reset } = useMutation({
    mutationFn: async ({
      file,
      attachmentMethod,
      classifyContext,
    }: {
      file: File;
      attachmentMethod: 'paste' | 'upload';
      classifyContext?: LessonContext;
    }): Promise<{
      materialAttachment: MaterialAttachment;
      classification?: 'embed' | 'inform';
    }> => {
      const fileId = generateUniqueId().toString();

      const uploadUrlResponse = await BackendService.instance.getUploadUrlV2({
        pathToFile: `game_files/${fileId}`,
        fileType: 'materialAttachment',
        mimeType: file.type,
        fileName: file.name,
        tenantId,
      });

      const url = uploadUrlResponse.data.url;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Content-Type', file.type);

        xhr.onreadystatechange = function() {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve(fileId);
            } else {
              reject(xhr.response);
            }
          }
        };
        xhr.onerror = function(response) {
          reject(response);
        };
        xhr.send(file);
      });
      const unprocessedFileUploadResponse = await BackendService.instance.markUploadSuccessfulV2(
        uploadUrlResponse.data.uploadId,
        tenantId,
      );
      const unprocessedFileUpload = unprocessedFileUploadResponse.fileUpload;
      if (file.type === 'text/plain') {
        return {
          materialAttachment: {
            attachmentMethod,
            fileUploadId: unprocessedFileUpload.id,
            fileName: file.name,
            mimetype: unprocessedFileUpload.mimeType,
            fileId: unprocessedFileUpload.fileId,
          },
        };
      }
      // anything that isn't a paste-in-text needs to be processed into a markdown file
      const processDocumentResponse = await BackendService.instance.materials.processDocument(
        {
          fileUpload: unprocessedFileUpload,
          classifyContext,
        },
      );
      const processedDocumentFileUpload = processDocumentResponse.fileUpload;

      return {
        classification: processDocumentResponse.classification,
        materialAttachment: {
          attachmentMethod,
          unprocessedAttachment: {
            fileUploadId: unprocessedFileUpload.id,
            fileName: file.name,
            mimetype: unprocessedFileUpload.mimeType,
            fileId: unprocessedFileUpload.fileId,
            attachmentMethod,
          },
          fileUploadId: processedDocumentFileUpload.id,
          fileName: processedDocumentFileUpload.fileId,
          mimetype: processedDocumentFileUpload.mimeType,
          fileId: processedDocumentFileUpload.fileId,
          passageId: processDocumentResponse.passageId,
          passageTitle: processDocumentResponse.passageTitle,
          classification: processDocumentResponse.classification,
        },
      };
    },
    onSuccess: () => {
      client.invalidateQueries(['uploadedDesignerImage']);
    },
  });

  return {
    upload,
    isUploading: isLoading,
    isSuccess: isSuccess,
    error: error,
    data,
    reset,
  };
};
